<template>
  <TwcToggle
    v-if="showToggle"
    v-model="toggleValue"
    :hint="toggleHint"
    :disabled="disableToggle"
    :reverse="true"
    @update:model-value="toggleTicketMode"
  >
    <template #label>
      <div>
        <label class="font-semibold mr-2">{{ toggleLabel }} </label>
        <ResponsiveTooltip :content-html="tooltipContent" :title="toggleLabel" position="center" />
      </div>
    </template>
  </TwcToggle>
</template>

<script setup lang="ts">
import { ref, watch, computed, onBeforeMount } from 'vue'
import { storeToRefs } from 'pinia'
import TwcToggle from '@/components/web-components/TwcToggle.vue'
import { useOrderStore } from '@/stores/order'
import { useSettingStore } from '@/stores/setting'
import appleUtils from '@/utils/apple-utils'
import ResponsiveTooltip from '@/components/elements/ResponsiveTooltip.vue'

const props = defineProps({
  maximumTickets: {
    type: Number,
    required: true
  }
})

const orderStore = useOrderStore()
const settingStore = useSettingStore()
const displayToggleError = ref(false)
const showToggle = ref(true)
const { passesQuantity, createIndividualPasses } = storeToRefs(orderStore)
const { settings } = storeToRefs(settingStore)
const tooltipIndividual = import.meta.env.VITE_TICKETS_PAGE_TOOLTIP_INDIVIDUAL_TICKETS
const tooltipGroup = import.meta.env.VITE_TICKETS_PAGE_TOOLTIP_GROUP_TICKET

const toggleValue = ref(false)

const tooltipContent = computed(() => {
  return showIndividualToggle.value ? tooltipIndividual : tooltipGroup
})

const disableToggle = computed(() => {
  return passesQuantity.value > props.maximumTickets
})

const showIndividualToggle = computed(() => {
  /**
   * When the default ticket type is set to 'group', we show the toggle
   * to allow the user to create individual tickets. Its kind of confusing
   * so take your time to understand it.
   */
  return settings.value.tickets_bundling_method === 'group'
})

const toggleLabel = computed(() => {
  return showIndividualToggle.value ? 'Create individual tickets' : 'Create a group ticket'
})

const toggleHint = computed(() => {
  return showIndividualToggle.value
    ? `One for each passenger and bike (${props.maximumTickets} max)`
    : `Once tickets are bundled, they cannot be used individually.`
})

function toggleTicketMode() {
  if (!disableToggle.value) {
    createIndividualPasses.value = showIndividualToggle.value
      ? toggleValue.value
      : !toggleValue.value
  } else {
    setDisplayToggleError()
  }
}

function setDisplayToggleError() {
  displayToggleError.value = disableToggle.value
}

watch(passesQuantity, () => (displayToggleError.value = false))

onBeforeMount(() => {
  if (appleUtils.isAppleMobile) {
    showToggle.value = appleUtils.isIosGreaterThan14
  }

  // Set the initial toggle state from settings if available
  if (settings.value && settings.value.tickets_bundling_method !== undefined) {
    createIndividualPasses.value = settings.value.tickets_bundling_method === 'individual'
  }
})
</script>

<style scoped></style>
